import React, { useState, useEffect, useRef } from "react";
import { Modal, Input, List, Spin, Empty, message, Space } from "antd";
import { CopyOutlined } from "@ant-design/icons";
// We open the create modal instead of server-side cloning
import { searchQualSteps } from "../../services/projectQualStepService";
import { useNavigate } from "react-router-dom";
import CreateQualificationStepModal from "./CreateQualificationStepModal";

export default function CloneQualificationStepModal({
  visible,
  onClose,
  onSuccess,
  projectId,
}) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [selectedQualStep, setSelectedQualStep] = useState(null);
  const debounceRef = useRef(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [initialForm, setInitialForm] = useState(null);

  // Search qualification steps with debounce
  useEffect(() => {
    const term = (search || "").trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (term.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const qualSteps = await searchQualSteps(term);
        setResults(Array.isArray(qualSteps) ? qualSteps : []);
      } catch (error) {
        message.error("Failed to search qualification steps");
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // No need for filtering since search is done on backend
  const filteredResults = results;

  // Handle qual step selection
  const handleSelectQualStep = (qualStep) => {
    setSelectedQualStep(qualStep);
  };

  // Handle "clone" by opening the Create Qualification Step modal prefilled
  const handleClone = async () => {
    if (!selectedQualStep) {
      message.warning("Please select a qualification step to clone");
      return;
    }

    setCloning(true);
    try {
      // Map the selected record to Create form fields
      const prefill = {
        name: selectedQualStep?.Name || "",
        status:
          selectedQualStep?.Project_Qualification_Step_Status__c || "Draft",
        qualificationStep: selectedQualStep?.Qualification_Step__c || "",
        funnel: selectedQualStep?.Funnel__c || "A",
        stepNumber: selectedQualStep?.Step_Number__c ?? "",
        numberOfAttempts: selectedQualStep?.Number_of_Attempts__c ?? "1",
        estimatedTimeMinutes:
          selectedQualStep?.Estimated_Time_to_Complete_mins__c ?? "",
        // Project/Objectives intentionally not prefilled to avoid mismatched links
      };

      setInitialForm(prefill);
      setShowCreateModal(true);

      // Reset and close search modal
      setSearch("");
      setSelectedQualStep(null);
      onClose && onClose();

      message.info(
        "Creation form opened with qualification step details. Review and Save.",
      );
    } catch (error) {
      message.error(
        "Failed to prepare step: " +
          (error.response?.data?.error?.message || error.message),
      );
      console.error("Prefill error:", error);
    } finally {
      setCloning(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setSelectedQualStep(null);
    onClose();
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <CopyOutlined />
            <span>Clone Qualification Step</span>
          </Space>
        }
        open={visible}
        onCancel={handleCancel}
        onOk={handleClone}
        okText="Clone Step"
        okButtonProps={{
          disabled: !selectedQualStep || cloning,
          loading: cloning,
        }}
        width={600}
        bodyStyle={{ padding: 16 }}
      >
        <div className="space-y-4">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search for a qualification step to clone:
            </label>
            <Input.Search
              placeholder="Search by name (min 3 characters)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
              size="large"
              loading={loading}
            />
          </div>

          {/* Qual Step Results */}
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: "4px",
              padding: "8px",
            }}
          >
            {loading ? (
              <div className="text-center py-8">
                <Spin tip="Loading qualification steps..." />
              </div>
            ) : filteredResults.length > 0 ? (
              <List
                dataSource={filteredResults}
                renderItem={(item) => (
                  <List.Item
                    className={`cursor-pointer transition-colors px-4 ${
                      selectedQualStep?.Id === item.Id
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => handleSelectQualStep(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <List.Item.Meta
                      title={
                        <div className="flex items-center">
                          {selectedQualStep?.Id === item.Id && (
                            <CopyOutlined className="mr-2 text-blue-500" />
                          )}
                          <span className="font-medium">{item.Name}</span>
                        </div>
                      }
                      description={
                        <div className="text-sm text-gray-500">
                          <div>Funnel: {item.Funnel__c || "N/A"}</div>
                          {item.Project__r?.Name && (
                            <div>Project: {item.Project__r.Name}</div>
                          )}
                          {item.Project_Objective__r?.Name && (
                            <div>
                              Objective: {item.Project_Objective__r.Name}
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : search.length >= 3 ? (
              <Empty
                description="No qualification steps found"
                className="py-8"
              />
            ) : (
              <Empty
                description="Type at least 3 characters to search"
                className="py-8"
              />
            )}
          </div>

          {/* Selected Qual Step Info */}
          {selectedQualStep && (
            <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
              <div className="text-sm">
                <div className="font-medium text-blue-900">
                  Selected: {selectedQualStep.Name}
                </div>
                <div className="text-blue-700 text-xs mt-1">
                  Will be cloned as: {selectedQualStep.Name} (Clone)
                </div>
              </div>
            </div>
          )}
        </div>
      </Modal>
      <CreateQualificationStepModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          if (onSuccess) onSuccess();
        }}
        initialValues={initialForm || {}}
      />
    </>
  );
}
